import React from 'react';
import { Form, Input, Select, Button, InputNumber, DatePicker, Spin } from 'antd';
import {
  EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined,
  DollarOutlined, FileTextOutlined, AppstoreOutlined, ProfileOutlined,
  CalendarOutlined, ClockCircleOutlined, UserOutlined,
} from '@ant-design/icons';
import { FaRupeeSign } from "react-icons/fa";
import dayjs from 'dayjs';
import GuardedButton from '../../components/guardedButton';
import expensesIcon from '../../../public/assets/expenses.png';

const { TextArea } = Input;

interface ExpenseDetailPanelProps {
  detail: any;
  loading: boolean;
  isEditing: boolean;
  canEdit: boolean;
  canDelete: boolean;
  saving: boolean;
  form: any;
  categoryOptions: Array<{ label: string; value: any }>;
  serviceOptions: Array<{ label: string; value: any }>;
  studentOptions: Array<{ label: string; value: any }>;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
}

const fmtDate = (v: any) => (v ? dayjs(v).format('DD MMM YYYY') : null);
const fmtDateTime = (v: any) => (v ? dayjs(v).format('DD MMM YYYY, hh:mm A') : null);
const money = (n: any) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;

const personName = (p: any) => (typeof p === 'object' && p !== null ? (p.name ?? `${p.firstName ?? ''}${p.lastName ? ' ' + p.lastName : ''}`.trim()) : p);

const Field: React.FC<{ icon?: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="cat-field">
      <span className="cat-field-label">{icon}{label}</span>
      <span className="cat-field-value">{isEmpty ? <span className="cat-muted">—</span> : value}</span>
    </div>
  );
};

const chip = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, border: '1px solid #77797a', borderRadius: '16px',
});

const ExpenseDetailPanel: React.FC<ExpenseDetailPanelProps> = ({
  detail, loading, isEditing, canEdit, canDelete, saving, form,
  categoryOptions, serviceOptions, studentOptions,
  onEdit, onCancel, onSave, onDelete,
}) => {
  const categoryName = detail?.category?.categoryName ?? detail?.categoryName
    ?? categoryOptions.find((o: any) => o.value === detail?.category)?.label;

  const serviceName = detail?.service?.serviceName ?? detail?.serviceName
    ?? serviceOptions.find((o: any) => o.value === detail?.service)?.label;
  const studentName = detail?.student
    ? `${detail.student.firstName || ''} ${detail.student.lastName || ''}`.trim()
    : detail?.studentName;
  const createdBy = personName(detail?.createdBy);
  const updatedBy = personName(detail?.updatedBy);

  const selectedCategory = Form.useWatch('category', form);

  const filteredServiceOptions = React.useMemo(() => {
    if (!selectedCategory) return [];
    return serviceOptions.filter(
      (opt: any) => {
        return opt.categoryId === selectedCategory;
      }
    );
  }, [serviceOptions, selectedCategory]);

  const handleCategoryChange = (value: any) => {
    form.setFieldsValue({ category: value ?? null });
    const currentService = form.getFieldValue('service');
    const stillValid = value && currentService && serviceOptions.some(
      (opt: any) => opt.value === currentService && opt.categoryId === value
    );
    if (!stillValid) {
      form.setFieldsValue({ service: null });
    }
  };

  return (
    <div className="cat-panel">
      <div className="cat-card">
        {/* Header */}
        <div className="cat-card-head">
          <div className="cat-head-left">
            <div className="cat-head-icon"><FaRupeeSign size={30}/></div>
            <div className="cat-head-titles">
              <div className="cat-head-name">{detail?.expenseName || '—'}</div>
              <div className="cat-head-meta"> 
                {detail?.expenseDate && <span className="cat-head-code"><CalendarOutlined /> {fmtDate(detail.expenseDate)}</span>}
              </div>
            </div>
          </div>

          <div className="cat-head-actions" onClick={(e) => e.stopPropagation()}>
            {!isEditing ? (
              <>
                {canEdit && (
                  <GuardedButton className="cat-btn cat-btn-edit" icon={<EditOutlined />} onClick={onEdit}>Edit</GuardedButton>
                )}
                {canDelete && (
                  <GuardedButton className="cat-btn cat-btn-delete" danger icon={<DeleteOutlined />} onClick={onDelete}>Delete</GuardedButton>
                )}
              </>
            ) : (
              <>
                <Button className="cat-btn" icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>
                <Button className="cat-btn" type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>Save Changes</Button>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="cat-card-body">
          {loading ? (
            <div className="cat-loading"><Spin /></div>
          ) : isEditing ? (
            <Form form={form} layout="vertical" className="cat-form">
              <div className="cat-section">
                <div className="cat-section-title">Expense Information</div>
                <div className="cat-grid cat-grid--3">
                  <Form.Item name="expenseName" label="Expense Name" rules={[{ required: true, message: 'Please enter expense name' }]}>
                    <Input placeholder="Enter expense name" />
                  </Form.Item>
                  <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Please enter amount' }]}>
                    <InputNumber prefix="₹" style={{ width: '100%' }} min={0} placeholder="Enter amount" />
                  </Form.Item>
                  <Form.Item name="category" label="Category">
                    <Select options={categoryOptions} placeholder="Select category" showSearch optionFilterProp="label" allowClear onChange={handleCategoryChange} onClear={() => handleCategoryChange(null)}/>
                  </Form.Item>
                  <Form.Item name="service" label="Service">
                    <Select options={filteredServiceOptions} placeholder={selectedCategory ? "Select service" : "Select category first"} showSearch optionFilterProp="label" allowClear disabled={!selectedCategory} onChange={(value) => form.setFieldsValue({ service: value ?? null })} onClear={() => form.setFieldsValue({ service: null })}/>
                  </Form.Item>
                  <Form.Item name="student" label="Student">
                    <Select options={studentOptions} placeholder="Select student" showSearch optionFilterProp="label" allowClear />
                  </Form.Item>
                  <Form.Item name="expenseDate" label="Expense Date">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                  <Form.Item name="description" label="Description"
                    className="cat-form-fullrow"
                    rules={[{ max: 500, message: 'Description cannot exceed 500 characters' }]}>
                    <TextArea
                      rows={4}
                      maxLength={500}
                      showCount
                      placeholder="Enter description"
                      style={{ color: '#262626' }}
                    />
                  </Form.Item>
                </div>
              </div>
            </Form>
          ) : (
            <>
              <div className="cat-section" style={{ padding: '18px 0 4px' }}>
                <div className="cat-grid cat-grid--3">
                  <Field icon={<FileTextOutlined />} label="Expense Name :" value={detail?.expenseName} />
                  <Field icon={<DollarOutlined />} label="Amount :" value={<span className="exp-amount-strong">{money(detail?.amount)}</span>} />
                  <Field icon={<AppstoreOutlined />} label="Category :" value={categoryName ? <span className="cat-chip" style={chip('#e6f4ff', '#1677ff')}>{categoryName}</span> : null} />
                  <Field icon={<ProfileOutlined />} label="Service :" value={serviceName ? <span className="cat-chip" style={chip('#f9f0ff', '#722ed1')}>{serviceName}</span> : null} />
                  <Field icon={<UserOutlined />} label="Student :" value={studentName} />
                  <Field icon={<CalendarOutlined />} label="Expense Date :" value={fmtDate(detail?.expenseDate)} />
                </div>
              </div>

              <div className="cat-section" style={{ borderTop: '1px solid #e5e8ee', padding: '18px 0 15px' }}>
                <div className="cat-grid cat-grid--3">
                  <Field icon={<UserOutlined />} label="Created By :" value={createdBy} />
                  <Field icon={<CalendarOutlined />} label="Created At :" value={fmtDateTime(detail?.createdAt)} />
                  <Field icon={<UserOutlined />} label="Updated By :" value={updatedBy} />
                  <Field icon={<ClockCircleOutlined />} label="Updated At :" value={fmtDateTime(detail?.updatedAt)} />
                </div>
              </div>

              <div className="cat-section" style={{ borderTop: '1px solid #e5e8ee', padding: '16px 0 4px' }}>
                <div className="cat-section-title"><FileTextOutlined /> Description</div>
                <TextArea
                  readOnly
                  value={detail?.description || ''}
                  placeholder="No description"
                  autoSize={{ minRows: 3, maxRows: 4 }}
                  style={{
                    marginTop: 8,
                    resize: 'none',
                    cursor: 'default',
                    backgroundColor: '#fafafa',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseDetailPanel;
